import { PIPELINE_BUDGET_CATEGORY_STRUCTURES } from "./pipeline-demo-structure";

/**
 * Locale-free demo scaffolding: ids, ordering, numbers, and styling.
 * Every user-visible string lives in `home.productDemos` and is read at render
 * time through `useHomeProductDemos`, so the demos follow the active locale.
 */

export const PIPELINE_DEMO_SCENE_NUMBER = 12;

/** Matches the script editor demo scene list (hospital through river bank). */
export const PIPELINE_DEMO_SCRIPT_SCENE_COUNT = 14;

export type PipelineDemoElement = {
  id: string;
  label: string;
  dept: string;
  color: string;
  highlightMatch?: string;
};

export type PipelineCoverageRubricRow = {
  id: string;
  label: string;
  score: 1 | 2 | 3 | 4;
};

export type PipelineStripboardRow = {
  id: string;
  sceneSequence: number;
  heading: string;
  summary: string;
  location: string;
  castIds: string;
  pages: string;
};

export type PipelineStripboardDayBreak = {
  type: "day";
  label: string;
};

export type PipelineStripboardEntry =
  | PipelineStripboardRow
  | PipelineStripboardDayBreak;

export const isStripboardDayBreak = (
  entry: PipelineStripboardEntry | undefined,
): entry is PipelineStripboardDayBreak =>
  entry !== undefined && "type" in entry;

export const getStripboardSceneRow = (
  rows: Record<string, PipelineStripboardEntry>,
  rowId: string,
): PipelineStripboardRow | null => {
  const entry = rows[rowId];
  if (!entry || isStripboardDayBreak(entry)) return null;
  return entry;
};

/** Script order with location splits: warehouse catwalk separated, corporate scenes split. */
export const PIPELINE_DEMO_STRIPBOARD_INITIAL_ORDER = [
  "day-01",
  "scene-1",
  "scene-2",
  "scene-3",
  "day-02",
  "scene-5",
  "scene-4",
  "scene-6",
  "scene-7",
  "day-03",
  "scene-9",
  "scene-8",
  "scene-10",
  "day-04",
  "scene-13",
  "scene-11",
  "scene-12",
  "day-05",
  "scene-14",
] as const;

/** AI reschedule demo: scene 12 grouped after scene 1; police station & office swapped (6↔7). */
export const PIPELINE_DEMO_STRIPBOARD_REORDERED = [
  "day-01",
  "scene-1",
  "scene-12",
  "scene-2",
  "scene-3",
  "day-02",
  "scene-5",
  "scene-4",
  "scene-7",
  "scene-6",
  "day-03",
  "scene-9",
  "scene-8",
  "scene-10",
  "day-04",
  "scene-13",
  "scene-11",
  "day-05",
  "scene-14",
] as const;

/** Numbers only. The localized `dateRange` comes from `useHomeProductDemos`. */
export const PIPELINE_DEMO_SHOOT_SUMMARY = {
  shootDays: 5,
  weeks: 3,
  sceneCount: PIPELINE_DEMO_SCRIPT_SCENE_COUNT,
  progressPercent: 42,
} as const;

export type PipelineBudgetSubLine = {
  label: string;
  estimate: number;
};

export type PipelineBudgetCategory = {
  id: string;
  name: string;
  estimate: number;
  bandClass: string;
  subLines?: PipelineBudgetSubLine[];
};

/** Estimates are locale-free, so the grand total does not need a translator. */
export const PIPELINE_DEMO_BUDGET_GRAND_TOTAL =
  PIPELINE_BUDGET_CATEGORY_STRUCTURES.reduce(
    (sum, category) => sum + category.estimate,
    0,
  );

export type PipelineBudgetGuild = {
  id: string;
  label: string;
  enabled: boolean;
};

export const PIPELINE_DEMO_SIDEBAR_CLASS =
  "flex w-[194px] shrink-0 flex-col border-r border-white/[0.05] bg-[#0F1117] sm:w-[216px]";
